import React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import { Link as RouterLink } from 'react-router-dom';
import LinearProgress from '@mui/material/LinearProgress';
import { styled } from '@mui/material/styles';
import BookIcon from '@mui/icons-material/Book';
import QuizIcon from '@mui/icons-material/Quiz';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useProgress } from '../hooks/useProgress';

// Styled components for modern design
const StyledCard = styled(Card)(({ theme }) => ({
  background: '#1a1a2e',
  borderRadius: '24px',
  border: '1px solid rgba(102, 126, 234, 0.1)',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.12)',
    border: '1px solid rgba(102, 126, 234, 0.2)',
  },
}));

const ModuleHeader = styled(Box)(({ theme, moduleColor }) => ({
  background: `linear-gradient(135deg, ${moduleColor}15 0%, ${moduleColor}25 100%)`,
  padding: theme.spacing(4),
  position: 'relative',
  borderBottom: `1px solid ${moduleColor}20`,
}));

const ModuleIcon = styled(Box)(({ theme, moduleColor }) => ({
  width: '56px',
  height: '56px',
  borderRadius: '16px',
  background: `linear-gradient(135deg, ${moduleColor} 0%, ${moduleColor}dd 100%)`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  boxShadow: `0 8px 24px ${moduleColor}30`,
  transition: 'all 0.3s ease',
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  padding: theme.spacing(1, 2),
  fontWeight: 600,
  textTransform: 'none',
  flex: 1,
  transition: 'all 0.2s ease',
}));

const PrimaryButton = styled(StyledButton)(({ theme, moduleColor }) => ({
  background: `linear-gradient(135deg, ${moduleColor} 0%, ${moduleColor}dd 100%)`,
  color: '#f1f5f9',
  boxShadow: `0 4px 12px ${moduleColor}30`,
  '&:hover': {
    background: `linear-gradient(135deg, ${moduleColor}dd 0%, ${moduleColor} 100%)`,
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 24px ${moduleColor}40`,
  },
}));

const SecondaryButton = styled(StyledButton)(({ theme, moduleColor }) => ({
  background: 'transparent',
  color: moduleColor || '#e2e8f0',
  border: `2px solid ${moduleColor || 'rgba(102, 126, 234, 0.1)'}30`,
  '&:hover': {
    background: `${moduleColor || '#000000'}08`,
    borderColor: moduleColor || 'rgba(102, 126, 234, 0.2)',
  },
}));

const ProgressWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, 3, 2),
}));

const CompletionBadge = styled(Chip)(({ theme, moduleColor }) => ({
  background: moduleColor,
  color: '#f1f5f9',
  fontWeight: 600,
  '& .MuiChip-icon': {
    color: '#f1f5f9',
  },
}));

export default function ModuleCard({ module }) {
  const progress = useProgress();
  const moduleProgress = progress[module.id] || { done: false, score: null };

  return (
    <StyledCard>
      <ModuleHeader moduleColor={module.color}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <ModuleIcon moduleColor={module.color}>
            <BookIcon sx={{ color: '#f1f5f9', fontSize: '1.5rem' }} />
          </ModuleIcon>
          {moduleProgress.done && (
            <CompletionBadge
              moduleColor={module.color}
              icon={<CheckCircleIcon />}
              label="Concluído"
              size="small"
            />
          )}
        </Box>
        
        <Typography 
          variant="h6" 
          component="h3" 
          sx={{ 
            fontWeight: 700, 
            color: '#f1f5f9',
            mb: 1,
            lineHeight: 1.3,
          }}
        >
          {module.title}
        </Typography>
        
        <Typography 
          variant="body2" 
          sx={{ 
            color: '#cbd5e1',
            fontWeight: 500,
          }}
        >
          Resumo completo • Quiz interativo
        </Typography>
      </ModuleHeader>

      <CardContent sx={{ flexGrow: 1, p: 3 }}>
        {moduleProgress.done ? (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CheckCircleIcon sx={{ color: module.color, fontSize: '1.2rem' }} />
              <Typography variant="body2" sx={{ color: '#e2e8f0', fontWeight: 600 }}>
                Quiz concluído
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
                Pontuação:
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: module.color }}>
                {moduleProgress.score}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={moduleProgress.score}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: 'rgba(102, 126, 234, 0.1)',
                '& .MuiLinearProgress-bar': {
                  background: `linear-gradient(90deg, ${module.color}, ${module.color}dd)`,
                  borderRadius: 4,
                },
              }}
            />
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" sx={{ color: '#cbd5e1', mb: 2 }}>
              Inicie seus estudos com o resumo e teste seus conhecimentos com o quiz.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PlayArrowIcon sx={{ color: module.color, fontSize: '1.2rem' }} />
              <Typography variant="body2" sx={{ color: '#e2e8f0', fontWeight: 600 }}>
                Pronto para começar
              </Typography>
            </Box>
          </Box>
        )}
      </CardContent>

      <CardActions sx={{ p: 3, pt: 0, gap: 2 }}>
        <PrimaryButton
          component={RouterLink}
          to={`/modulos/${module.id}`}
          moduleColor={module.color}
          startIcon={<BookIcon />}
          fullWidth
        >
          Estudar
        </PrimaryButton>
        <SecondaryButton
          component={RouterLink}
          to={`/modulos/${module.id}/quiz`}
          startIcon={<QuizIcon />}
          moduleColor={module.color}
          fullWidth
        >
          Quiz
        </SecondaryButton>
      </CardActions>
    </StyledCard>
  );
}
